import { Injectable } from '@angular/core';
import { FetchPolicy } from '@apollo/client';
import { of } from 'rxjs';
import { map, tap, switchMap } from 'rxjs/operators';

import { throwOnGraphqlError } from '@app/core';
import { Whitelabel } from '@app/models';
import { AppService } from './app.service';
import {
  ActivateWhitelabelDomainGQL,
  ActivateWhitelabelSendgridGQL,
  UpdateWhitelabelAppearanceGQL,
  UpdateWhitelabelAppearanceMutationVariables,
  UpdateWhitelabelDomainGQL,
  UpdateWhitelabelDomainMutationVariables,
  UpdateWhitelabelOpengraphGQL,
  UpdateWhitelabelOpengraphMutationVariables,
  UpdateWhitelabelSendgridGQL,
  UpdateWhitelabelSendgridMutationVariables,
  WhitelabelAppearanceInput,
  WhitelabelDomainVerifiedGQL,
  WhitelabelDomainVerifiedQueryVariables,
  WhitelabelGQL,
  WhitelabelSendgridDnsGQL,
  WhitelabelSendgridDnsQueryVariables,
  WhitelabelSendgridValidateGQL,
  WhitelabelSendgridValidateMutationVariables
} from 'src/generated/graphql.default';
import { WhitelabelByDomainGQL, WhitelabelByDocumentGQL, WhitelabelByDocumentQueryVariables } from 'src/generated/graphql.public';

@Injectable({ providedIn: 'root' })
export class WhitelabelService {
  logo = '/assets/Autentique.svg';
  pdfLogo = '/assets/autentique-logo.svg';
  pdfLogoMini = '/assets/autentique-logo.svg';
  mainColor = '#3379F2';
  appearance: WhitelabelAppearanceInput = {};
  isApplicationWhitelabelLoaded = false;
  private _whitelabel: Whitelabel;

  constructor(
    private whitelabelGQL: WhitelabelGQL,
    private whitelabelByDomainGQL: WhitelabelByDomainGQL,
    private whitelabelByDocumentGQL: WhitelabelByDocumentGQL,
    private whitelabelSendgridDnsGQL: WhitelabelSendgridDnsGQL,
    private whitelabelDomainVerifiedGQL: WhitelabelDomainVerifiedGQL,
    private updateWhitelabelAppearanceGQL: UpdateWhitelabelAppearanceGQL,
    private updateWhitelabelSendgridGQL: UpdateWhitelabelSendgridGQL,
    private updateWhitelabelDomainGQL: UpdateWhitelabelDomainGQL,
    private whitelabelSendgridValidateGQL: WhitelabelSendgridValidateGQL,
    private activateWhitelabelSendgridGQL: ActivateWhitelabelSendgridGQL,
    private activateWhitelabelDomainGQL: ActivateWhitelabelDomainGQL,
    private updateWhitelabelOpengraphGQL: UpdateWhitelabelOpengraphGQL,
    private appService: AppService
  ) {}

  get whitelabel(): Whitelabel {
    return this._whitelabel;
  }

  // Used only on Configurations
  loadCurrentWhitelabel() {
    return of(null).pipe(
      switchMap(() => this.whitelabelGQL.fetch(null, { fetchPolicy: 'no-cache' })),
      throwOnGraphqlError(),
      map(response => response.data.whitelabel)
    );
  }

  // Used on all Application
  loadWhitelabelByDomain(options: { fetchPolicy: FetchPolicy } = { fetchPolicy: 'network-only' }) {
    return of(null).pipe(
      switchMap(() => this.whitelabelByDomainGQL.fetch({ domain: location.hostname }, { fetchPolicy: options.fetchPolicy })),
      throwOnGraphqlError(),
      map(response => response.data.whitelabelByDomain),
      tap(whitelabel => {
        this._whitelabel = this.buildWhitelabel(whitelabel);
        this.appService.isDarkmode = whitelabel.is_darkmode;
        this.setMetaTags();
      })
    );
  }

  // Used on Document Whitelabel
  loadWhitelabelByDocument(variables: WhitelabelByDocumentQueryVariables, options: { fetchPolicy: FetchPolicy } = { fetchPolicy: 'network-only' }) {
    return of(null).pipe(
      switchMap(() => this.whitelabelByDocumentGQL.fetch(variables, options)),
      throwOnGraphqlError(),
      map(response => response.data.whitelabelByDocument)
    );
  }

  buildWhitelabel(whitelabel?: Whitelabel): Whitelabel {
    return {
      domain: whitelabel?.domain || null,
      sendgrid_domain: whitelabel?.sendgrid_domain || null,
      sendgrid_email_user: whitelabel?.sendgrid_email_user || null,
      is_darkmode: typeof (whitelabel || {}).is_darkmode === 'boolean' ? whitelabel.is_darkmode : false,
      main_color: whitelabel?.main_color || this.mainColor,
      logo: whitelabel?.logo || this.logo,
      pdf_logo: whitelabel?.pdf_logo || this.pdfLogo,
      pdf_logo_mini: whitelabel?.pdf_logo_mini || this.pdfLogoMini
    };
  }

  resetWhitelabelInfo() {
    this._whitelabel = this.buildWhitelabel();
  }

  setMetaTags(options: { iconUrl?: string } = {}) {
    if (this.appService.isAutentiqueDomain()) {
      const excludedRegexp = /^x-/;
      document.querySelectorAll('head link, head meta').forEach(item => {
        ['rel', 'name', 'property'].forEach(attrName => {
          if (excludedRegexp.test(item.getAttribute(attrName))) {
            item.setAttribute(attrName, item.getAttribute(attrName).replace(excludedRegexp, ''));
          }
        });
      });
    } else {
      const iconUrl = options.iconUrl || this.whitelabel.pdf_logo_mini;

      const newLink = document.createElement('link');
      newLink.rel = 'icon';
      newLink.type = 'image/png';
      newLink.href = iconUrl === this.pdfLogoMini ? '/assets/icons/android-icon-192x192.png' : iconUrl;
      newLink.setAttribute('class', 'favicon');
      document.head.appendChild(newLink);
    }
  }

  whitelabelDomainVerified(domain: WhitelabelDomainVerifiedQueryVariables) {
    return this.whitelabelDomainVerifiedGQL.fetch(domain).pipe(
      throwOnGraphqlError(),
      map(response => response.data.whitelabelDomainVerified)
    );
  }

  sendgridDns(sendgridDomain: WhitelabelSendgridDnsQueryVariables) {
    return this.whitelabelSendgridDnsGQL.fetch(sendgridDomain).pipe(
      throwOnGraphqlError(),
      map(response => response.data.whitelabelSendgridDns)
    );
  }

  updateWhitelabelAppearance(whitelabel: UpdateWhitelabelAppearanceMutationVariables) {
    return this.updateWhitelabelAppearanceGQL.mutate(whitelabel).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateWhitelabelAppearance)
    );
  }

  updateWhitelabelSendgrid(whitelabel: UpdateWhitelabelSendgridMutationVariables) {
    return this.updateWhitelabelSendgridGQL.mutate(whitelabel).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateWhitelabelSendgrid)
    );
  }

  updateWhitelabelDomain(domain: UpdateWhitelabelDomainMutationVariables) {
    return this.updateWhitelabelDomainGQL.mutate(domain).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateWhitelabelDomain)
    );
  }

  updateWhitelabelOpenGraph(whitelabelOpengraph: UpdateWhitelabelOpengraphMutationVariables) {
    return this.updateWhitelabelOpengraphGQL.mutate(whitelabelOpengraph).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateWhitelabelOpengraph)
    );
  }

  whitelabelSendgridValidate(sendgrid_domain: WhitelabelSendgridValidateMutationVariables) {
    return this.whitelabelSendgridValidateGQL.mutate(sendgrid_domain).pipe(
      throwOnGraphqlError(),
      map(response => response.data.whitelabelSendgridValidate)
    );
  }

  activateWhitelabelSendgrid() {
    return this.activateWhitelabelSendgridGQL.mutate().pipe(throwOnGraphqlError());
  }

  activateWhitelabelDomain() {
    return this.activateWhitelabelDomainGQL.mutate().pipe(throwOnGraphqlError());
  }

  isWhitelabel() {
    if (!this.appService.isEnterpriseChild && this.isApplicationWhitelabelLoaded) {
      const conditions: boolean[] = [
        !!this.whitelabel.domain,
        this.logo !== this.whitelabel.logo,
        this.pdfLogo !== this.whitelabel.pdf_logo,
        this.pdfLogoMini !== this.whitelabel.pdf_logo_mini,
        this.mainColor !== this.whitelabel.main_color
      ];

      return conditions.includes(true);
    }

    return this.appService.isEnterpriseChild;
  }
}
